import api from './api';

export interface SignupData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  description?: string;
  organization?: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface ResetRequestData {
  email: string;
}

export interface PasswordResetData {
  token: string;
  new_password: string;
}

export interface ChangePasswordData {
  email: string;
  old_password: string;
  new_password: string;
}

export interface UpdateDetailsData {
  email: string;
  first_name: string;
  last_name: string;
  description?: string;
  organization?: string;
}

export interface User {
  email: string;
  first_name: string;
  last_name: string;
  description?: string;
  organization?: string;
  is_admin?: boolean;
  consent_accepted?: boolean;
  consent_viewed?: boolean;
}

export interface AdminUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  description?: string;
  organization?: string;
  is_active: boolean;
  is_approved: boolean;
  is_admin: boolean;
  consent_accepted: boolean;
  consent_viewed: boolean;
  subscription_tier?: string;
  subscription_expires?: number;
}

export interface AuthResponse {
  status: string;
  message?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  description?: string;
  organization?: string;
}

const AuthService = {
  // Signup new user
  signup: async (data: SignupData): Promise<AuthResponse> => {
    const response = await api.post('/auth/signup', data);
    return response.data;
  },
  
  // Login user
  login: async (data: LoginData): Promise<User> => {
    const response = await api.post('/auth/login', data);
    localStorage.setItem('user', JSON.stringify(response.data));
    return response.data;
  },
  
  // Logout user
  logout: (): void => {
    localStorage.removeItem('user');
  },
  
  // Get current user
  getCurrentUser: (): User | null => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      return JSON.parse(userStr);
    }
    return null;
  },
  
  // Request password reset
  requestPasswordReset: async (data: ResetRequestData): Promise<AuthResponse> => {
    const response = await api.post('/auth/password_reset_request', data);
    return response.data;
  },
  
  // Submit password reset
  submitPasswordReset: async (data: PasswordResetData): Promise<AuthResponse> => {
    const response = await api.post('/auth/password_reset_submit', data);
    return response.data;
  },
  
  // Change password
  changePassword: async (data: ChangePasswordData): Promise<AuthResponse> => {
    const response = await api.post('/auth/change_password', data);
    return response.data;
  },
  
  // Update user details
  updateDetails: async (data: UpdateDetailsData): Promise<AuthResponse> => {
    const response = await api.post('/auth/update_details', data);
    return response.data;
  },
  
  // Update consent
  updateConsent: async (email: string, consent_accepted: boolean): Promise<AuthResponse> => {
    const response = await api.post('/auth/update_consent', { email, consent_accepted });
    return response.data;
  },
  
  // Check if email exists
  checkEmail: async (email: string): Promise<{exists: boolean}> => {
    const response = await api.post('/auth/check_email', { email });
    return response.data;
  },
  
  // Confirm email
  confirmEmail: async (token: string): Promise<AuthResponse> => {
    const response = await api.get(`/auth/confirm?token=${token}`);
    return response.data;
  },
  
  // Approve user via tokenized link
  approveUser: async (email: string, token: string): Promise<AuthResponse> => {
    const response = await api.post('/auth/approve', { email, token });
    return response.data;
  },
  
  // Admin methods
  getAllUsers: async (): Promise<{users: AdminUser[]}> => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const response = await api.get(`/auth/admin/users?admin_email=${user.email}`);
    return response.data;
  },

  exportUsers: async (): Promise<Blob> => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const response = await api.get(`/auth/admin/users/export?admin_email=${user.email}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  exportUserChats: async (userEmail: string): Promise<Blob> => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const response = await api.get(`/auth/admin/users/${userEmail}/chats/export?admin_email=${user.email}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  exportUserDrafts: async (userEmail: string): Promise<Blob> => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const response = await api.get(`/auth/admin/users/${userEmail}/drafts/export?admin_email=${user.email}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  exportAllChats: async (): Promise<Blob> => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const response = await api.get(`/auth/admin/export/chats?admin_email=${user.email}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  exportAllDrafts: async (): Promise<Blob> => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const response = await api.get(`/auth/admin/export/drafts?admin_email=${user.email}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  exportUserAnalysis: async (userEmail: string): Promise<Blob> => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const response = await api.get(`/auth/admin/users/${userEmail}/analysis/export?admin_email=${user.email}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  exportAllAnalysis: async (): Promise<Blob> => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const response = await api.get(`/auth/admin/export/analysis?admin_email=${user.email}`, {
      responseType: 'blob'
    });
    return response.data;
  },
  
  adminApproveUser: async (email: string): Promise<AuthResponse> => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const response = await api.post(`/auth/admin/approve_user?admin_email=${user.email}`, { email });
    return response.data;
  },
  
  adminArchiveUser: async (email: string): Promise<AuthResponse> => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const response = await api.post(`/auth/admin/archive_user?admin_email=${user.email}`, { email });
    return response.data;
  },
  
  toggleAdminStatus: async (email: string): Promise<AuthResponse> => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const response = await api.post(`/auth/admin/toggle_admin?admin_email=${user.email}`, { email });
    return response.data;
  },

  // Premium subscription management (admin only)
  adminSetPremium: async (email: string, days: number = 365): Promise<any> => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const response = await api.post(`/auth/admin/set-premium?admin_email=${user.email}`, {
      user_email: email,
      days
    });
    return response.data;
  },

  adminRevokePremium: async (email: string): Promise<any> => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const response = await api.post(`/auth/admin/revoke-premium?admin_email=${user.email}`, {
      user_email: email
    });
    return response.data;
  },

  adminGetUserSubscription: async (email: string): Promise<any> => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const response = await api.get(`/auth/admin/user/${email}/subscription?admin_email=${user.email}`);
    return response.data;
  },

  adminGetAllSubscriptions: async (): Promise<any> => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const response = await api.get(`/auth/admin/users/subscriptions/bulk?admin_email=${user.email}`);
    return response.data;
  }
};

export default AuthService;
